"use client";

import { useCallback, useState, useTransition } from "react";
import { usePathname, useRouter } from "next/navigation";
import { RefreshCw } from "lucide-react";
import { useTranslations } from "next-intl";
import {
  APP_SPLASH_ELEMENT_ID,
  readInstalledAppShell,
  reloadCurrentDocument,
  revealAppSplash,
  useDevicePlatform,
  usePullToRefresh,
  useStandaloneDisplay,
  useStaleResumeRefresh,
} from "@award-party/shared";
import { PullToRefreshIndicator } from "@/components/pull-to-refresh-indicator";
import {
  pathnameOffersStaleResumeRefresh,
  shouldOfferStaleResumePrompt,
} from "@/lib/stale-resume-prompt";
import { refreshAppRouterSafely } from "@/lib/safari-network-load-error";

export function PwaRefresh() {
  const router = useRouter();
  const pathname = usePathname();
  const standalone = useStandaloneDisplay();
  const { platform } = useDevicePlatform();
  const t = useTranslations("StaleResume");
  const [promptPath, setPromptPath] = useState<string | null>(null);
  const [seenPathname, setSeenPathname] = useState(pathname);
  const [pending, startTransition] = useTransition();

  if (pathname !== seenPathname) {
    setSeenPathname(pathname);
    setPromptPath(null);
  }

  const offerRefresh = useCallback(() => {
    if (!pathnameOffersStaleResumeRefresh(pathname)) {
      return;
    }
    setPromptPath(pathname);
  }, [pathname]);

  const hardReload = useCallback(() => {
    reloadCurrentDocument(window.location);
  }, []);

  const refreshFromPrompt = useCallback(() => {
    if (readInstalledAppShell()) {
      revealAppSplash(document.getElementById(APP_SPLASH_ELEMENT_ID));
      reloadCurrentDocument(window.location);
      return;
    }

    startTransition(async () => {
      try {
        await refreshAppRouterSafely(() => router.refresh());
        setPromptPath(null);
      } catch {
        // Leave the prompt up so a failed refresh can be tried again.
      }
    });
  }, [router]);

  const pullToRefresh = usePullToRefresh({
    onRefresh: hardReload,
    enabled: standalone,
  });
  useStaleResumeRefresh({
    onRefresh: offerRefresh,
    enabled: shouldOfferStaleResumePrompt({
      installedApp: standalone,
      platform,
    }),
  });

  const showPrompt =
    promptPath !== null &&
    promptPath === pathname &&
    pathnameOffersStaleResumeRefresh(pathname);

  return (
    <>
      <PullToRefreshIndicator
        status={pullToRefresh.status}
        offset={pullToRefresh.offset}
        progress={pullToRefresh.progress}
      />
      {showPrompt ? (
        <div className="pointer-events-none fixed inset-x-0 top-[calc(4.75rem+max(env(safe-area-inset-top,0px),var(--safe-area-inset-top,0px)))] z-[60] flex justify-center px-4">
          <div
            role="status"
            aria-live="polite"
            data-testid="stale-resume-prompt"
            className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl border border-(--mk-gold-border) bg-(--mk-ink-soft)/95 px-4 py-3 shadow-lg backdrop-blur-md"
          >
            <p className="min-w-0 flex-1 text-sm font-medium text-(--mk-cream)">
              {t("title")}
            </p>
            <button
              type="button"
              onClick={refreshFromPrompt}
              disabled={pending}
              aria-busy={pending}
              className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-full bg-(--mk-gold) px-4 py-2 text-sm font-semibold text-(--mk-ink) disabled:cursor-progress disabled:opacity-70"
            >
              <RefreshCw
                className={`h-4 w-4 ${pending ? "animate-spin" : ""}`}
                aria-hidden="true"
              />
              {pending ? t("refreshing") : t("refresh")}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
